import React from 'react'
import { useNavigate } from 'react-router-dom'

import { ProductImage } from 'app/common/Common'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { OrdersContext } from 'contexts/OrdersContext'
import {
  DcBreadcrumbs,
  DcFlexBox,
  DcOverlaySlider,
  DcSwitchTab,
  DcTabs,
  PixelFlexBox,
  PixelPageContainer,
  PixelTable,
  Section,
  SectionContent,
  colors,
} from 'pixel-base'
import queryString from 'query-string'
import { AjaxResponse } from 'rxjs/ajax'
import styled from 'styled-components'
import {
  getGroupedWarehouseInventory,
  getWarehouseInventory,
} from 'utils/api-routes.util'

import GroupedProductVariations, {
  GroupedParentProduct,
  GroupedProductVariationsHandle,
} from './GroupedProductVariations'
import InventoryProductSearch from './InventoryProductSearch'
import EditQuantity from './subcomponents/EditQuantity'
import {
  CenteredInventoryCellValue,
  InventoryProductNameCell,
  InventorySkuCell,
  inventoryFitWidthTableStyles,
} from './WarehouseInventoryProductCell'

const inventoryTableStyles = {
  ...inventoryFitWidthTableStyles,
  head: {
    style: {
      zIndex: 5,
      backgroundColor: colors.white,
    },
  },
  headRow: {
    style: {
      backgroundColor: colors.white,
    },
  },
}

const AdjustInventory: React.FC = () => {
  const { state } = React.useContext(OrdersContext)
  const [inventory, setInventory] = React.useState()
  const [tableLoading, setTableLoader] = React.useState(true)
  const [warehousesList, setWarehousesList] = React.useState([])
  const p = queryString.parse(window.location.search)
  const [filters, setFilters] = React.useState({
    warehouse_id: p.warehouse_id ? Number(p.warehouse_id) : '',
    page_size: Number(p.page_size) || 50,
    page: Number(p.page) || 0,
    search: (p.search as string) || '',
    searchBySKU: p.searchBySKU === '1' || p.searchBySKU === 'true',
    view: p.view === 'grouped' ? 'grouped' : 'individual',
  })
  const isGroupedView = filters.view === 'grouped'
  const [selectedGroupedProduct, setSelectedGroupedProduct] =
    React.useState<GroupedParentProduct | null>(null)
  const groupedDrawerRef = React.useRef<GroupedProductVariationsHandle>(null)
  const requestGeneration = React.useRef(0)
  const closeGroupedDrawer = React.useCallback(() => {
    setSelectedGroupedProduct(null)
  }, [])
  const requestCloseGroupedDrawer = React.useCallback(() => {
    if (groupedDrawerRef.current) {
      groupedDrawerRef.current.requestClose()
      return
    }
    closeGroupedDrawer()
  }, [closeGroupedDrawer])
  const breadcrumbs = [
    {
      name: 'Warehouse List',
      link: '/inventory',
    },
    {
      name: 'Warehouse Inventory',
      link: `/inventory/warehouse-wise?page=0&page_size=50&search=&warehouse_id=${filters.warehouse_id} `,
    },
    {
      name: 'Manually Inventory Update',
      active: true,
    },
  ]
  const [totalRows, setTotalRows] = React.useState(0)
  const [resetPaginationToggle, setResetPaginationToggle] =
    React.useState(false)
  const navigate = useNavigate()
  interface IWarehouseInventory {
    created_at: {
      date: string
      timezone_type: number
      timezone: string
    }
    on_hand: number
    committed: number
    available: number
    unavailable: number
  }

  interface IProductData {
    product_id: number
    product_name: string
    product_sku: string
    product_image: string
    warehouse_inventory: IWarehouseInventory
    variation_count?: number
  }

  const fetchInventory = () => {
    const requestId = ++requestGeneration.current
    setTableLoader(true)
    const request = isGroupedView
      ? getGroupedWarehouseInventory(filters)
      : getWarehouseInventory(filters)
    request.subscribe((res) => {
      if (requestId !== requestGeneration.current) {
        return
      }
      const typedRes = res as AjaxResponse<{
        response: { data: IProductData[]; _meta: { total: number } }
      }>
      const rows = typedRes.response.data || []
      setInventory(
        isGroupedView
          ? rows.map((row: IProductData) => ({ ...row, id: row.product_id }))
          : rows,
      )
      setTotalRows(typedRes.response._meta.total)
      setTableLoader(false)
    })
  }

  React.useEffect(() => {
    if (state?.warehouses?.length) {
      setTableLoader(true)
      setWarehousesList(state?.warehouses)
      if (filters.warehouse_id === '') {
        setFilters((currentFilters) => ({
          ...currentFilters,
          warehouse_id: state?.warehouses[0].wh_id,
        }))
      }

      setTableLoader(false)
    }
  }, [state])
  React.useEffect(() => {
    navigate({
      pathname: '/inventory/edit',
      search: queryString.stringify({
        ...filters,
        searchBySKU: filters.searchBySKU ? 1 : undefined,
        view: isGroupedView ? 'grouped' : undefined,
      }),
    })
    setSelectedGroupedProduct(null)
    if (filters.warehouse_id !== '') {
      fetchInventory()
    }
  }, [filters])

  const individualColumns = [
    {
      dataKey: 'product_image',
      name: 'Image',
      width: '8%',
      center: true,
      selector: (row) => (
        <CenteredInventoryCellValue>
          <ProductImage
            style={{
              width: '32px',
              marginRight: 0,
              flexShrink: 0,
            }}
            path={row.product_image}
          />
        </CenteredInventoryCellValue>
      ),
    },
    {
      dataKey: 'product_name',
      name: 'Product Name',
      width: '22%',
      selector: (row) => <InventoryProductNameCell name={row.product_name} />,
    },
    {
      dataKey: 'product_sku',
      name: 'SKU',
      sortable: true,
      width: '14%',
      selector: (row) => <InventorySkuCell sku={row.product_sku} />,
    },
    {
      dataKey: 'unavailable',
      name: 'Unavailable',
      width: '8%',
      sortable: true,
      center: true,
      selector: (row) => (
        <CenteredInventoryCellValue>
          {row.warehouse_inventory.unavailable != null
            ? row.warehouse_inventory.unavailable
            : '0'}
        </CenteredInventoryCellValue>
      ),
    },
    {
      dataKey: 'committed',
      name: 'Committed',
      width: '8%',
      sortable: true,
      center: true,
      selector: (row) => (
        <CenteredInventoryCellValue>
          {row.warehouse_inventory.committed != null
            ? row.warehouse_inventory.committed
            : '0'}
        </CenteredInventoryCellValue>
      ),
    },
    {
      dataKey: 'available',
      name: 'Available',
      width: '8%',
      sortable: true,
      center: true,
      selector: (row) => (
        <CenteredInventoryCellValue>
          {row.warehouse_inventory.available != null
            ? row.warehouse_inventory.available
            : '0'}
        </CenteredInventoryCellValue>
      ),
    },
    {
      dataKey: 'on_hand',
      name: 'On Hand',
      width: '8%',
      sortable: true,
      center: true,
      selector: (row) => (
        <CenteredInventoryCellValue>
          {row.warehouse_inventory.on_hand != null
            ? row.warehouse_inventory.on_hand
            : '0'}
        </CenteredInventoryCellValue>
      ),
    },
    {
      dataKey: 'edit_available',
      name: 'Edit Quantity',
      sortable: false,
      width: '24%',
      selector: (row) => (
        <EditQuantity
          handleUpdate={handleUpdate}
          id={filters.warehouse_id}
          product_id={row.product_id}
        />
      ),
    },
  ]

  const columns = isGroupedView
    ? [
        {
          dataKey: 'product_image',
          name: 'Image',
          width: '8%',
          center: true,
          selector: (row) => (
            <GroupedImageCell>
              <ProductImage
                style={{
                  width: '48px',
                  height: '48px',
                  marginRight: 0,
                  objectFit: 'contain',
                }}
                path={row.product_image}
              />
            </GroupedImageCell>
          ),
        },
        {
          dataKey: 'product_name',
          name: 'Product Name',
          width: '24%',
          selector: (row) => (
            <GroupedProductNameCell
              name={row.product_name}
              variationCount={row.variation_count}
            />
          ),
        },
        {
          dataKey: 'product_sku',
          name: 'SKU',
          sortable: true,
          width: '14%',
          selector: (row) => <InventorySkuCell sku={row.product_sku} />,
        },
        {
          dataKey: 'column_gap',
          name: '',
          width: '8%',
          sortable: false,
          selector: () => null,
        },
        {
          dataKey: 'unavailable',
          name: 'Unavailable',
          width: '10%',
          center: true,
          selector: (row) => (
            <CenteredInventoryCellValue>
              {groupedInventoryValue(row, 'unavailable')}
            </CenteredInventoryCellValue>
          ),
        },
        {
          dataKey: 'committed',
          name: 'Committed',
          width: '10%',
          center: true,
          selector: (row) => (
            <CenteredInventoryCellValue>
              {groupedInventoryValue(row, 'committed')}
            </CenteredInventoryCellValue>
          ),
        },
        {
          dataKey: 'available',
          name: 'Available',
          width: '10%',
          center: true,
          selector: (row) => (
            <CenteredInventoryCellValue>
              {groupedInventoryValue(row, 'available')}
            </CenteredInventoryCellValue>
          ),
        },
        {
          dataKey: 'on_hand',
          name: 'On Hand',
          width: '10%',
          center: true,
          selector: (row) => (
            <CenteredInventoryCellValue>
              {groupedInventoryValue(row, 'on_hand')}
            </CenteredInventoryCellValue>
          ),
        },
      ]
    : individualColumns
  const handlePageChange = (page) => {
    if (filters.page === page - 1) return
    setFilters({
      ...filters,
      page: page - 1,
    })
  }
  const handlePerRowsChange = async (newPerPage) => {
    setFilters({
      ...filters,
      page_size: newPerPage,
    })
  }
  const handleUpdate = (newData) => {
    fetchInventory()
    //swape the obj match the product_id
    const updatedInventory = inventory.map((item) => {
      if (item.product_id === newData.product_id) {
        item.warehouse_inventory.available = newData.available
        item.warehouse_inventory.unavailable = newData.unavailable
        item.warehouse_inventory.committed = newData.committed
        item.warehouse_inventory.on_hand = newData.on_hand
      }
      return item
    })
    setInventory(updatedInventory)
  }

  return (
    <>
      <DcFlexBox
        height="60px"
        fontSize="16px"
        style={{
          fontSize: '16px',
          padding: '8px opx',
          borderBottom: '1px solid #e5e5e5',
        }}
      >
        <DcFlexBox
          justifyContent="space-between"
          alignItems="center"
          padding=".5rem 20px"
        >
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
          <ProfileSection />
        </DcFlexBox>
      </DcFlexBox>

      <PixelPageContainer padding="0px">
        <Section style={{ padding: '0px' }}>
          <SectionContent style={{ padding: '0px', borderRadius: '0px' }}>
            <DcFlexBox
              height="auto"
              padding="0px 20px"
              justifyContent="space-between"
              alignItems="flex-end"
            >
              <DcFlexBox height="100%" alignItems="flex-end" width="50%">
                <DcTabs
                  size="md"
                  tabs={warehousesList.map((warehouse: any) => {
                    return {
                      label: warehouse.name,
                      value: warehouse.wh_id,
                    }
                  })}
                  activeTab={Number(filters.warehouse_id)}
                  handleChange={(value) => {
                    setSelectedGroupedProduct(null)
                    setFilters({
                      ...filters,
                      warehouse_id: value,
                    })
                  }}
                />
              </DcFlexBox>
              <PixelFlexBox
                justifyContent="flex-end"
                alignItems="center"
                alignContent="center"
                gap="10px"
                width="50%"
                padding="5px 0px"
              >
                <InventoryProductSearch
                  search={filters.search}
                  searchBySKU={filters.searchBySKU}
                  onSearchModeChange={(searchBySKU) =>
                    setFilters({
                      ...filters,
                      searchBySKU,
                      ...(isGroupedView ? { page: 0 } : {}),
                    })
                  }
                  onSearchChange={(search) => {
                    setResetPaginationToggle(search.length > 0)
                    setFilters({
                      ...filters,
                      search,
                      ...(isGroupedView ? { page: 0 } : {}),
                    })
                  }}
                />
                <InventoryViewSwitchTab
                  width="max-content"
                  trackPadding="1px"
                  trackBorderRadius="4px"
                  tabBorderRadius="4px"
                  tabPadding="4px 10px"
                  tabFontSize="14px"
                  tabs={[
                    { value: 'individual', label: 'Individual' },
                    { value: 'grouped', label: 'Group by Product' },
                  ]}
                  value={filters.view}
                  onChange={(value) => {
                    setSelectedGroupedProduct(null)
                    setInventory(undefined)
                    setTableLoader(true)
                    setFilters({
                      ...filters,
                      view: value === 'grouped' ? 'grouped' : 'individual',
                      page: 0,
                    })
                  }}
                />
              </PixelFlexBox>
            </DcFlexBox>
          </SectionContent>
        </Section>
        <Section style={{ padding: '20px' }}>
          <SectionContent>
            <PixelTable
              key={filters.view}
              pagination={true}
              paginationServer
              columns={columns}
              data={inventory}
              progressPending={tableLoading}
              paginationRowsPerPageOptions={[50, 100, 250, 500]}
              paginationPerPage={filters.page_size}
              customStyles={inventoryTableStyles}
              fixedHeader
              fixedHeaderScrollHeight="calc(100vh - 216px)"
              onChangePage={handlePageChange}
              onChangeRowsPerPage={handlePerRowsChange}
              paginationResetDefaultPage={resetPaginationToggle}
              paginationTotalRows={totalRows}
              pointerOnHover={isGroupedView}
              highlightOnHover={isGroupedView}
              onRowClicked={
                isGroupedView
                  ? (row) => setSelectedGroupedProduct(row)
                  : undefined
              }
            />
          </SectionContent>
        </Section>
      </PixelPageContainer>
      <DcOverlaySlider
        isOpen={Boolean(isGroupedView && selectedGroupedProduct)}
        onClose={requestCloseGroupedDrawer}
        title="Adjust Inventory"
        width="70%"
        padding="0px"
      >
        {selectedGroupedProduct && (
          <GroupedProductVariations
            ref={groupedDrawerRef}
            data={selectedGroupedProduct}
            warehouseId={filters.warehouse_id}
            onSaved={fetchInventory}
            onClose={closeGroupedDrawer}
          />
        )}
      </DcOverlaySlider>
    </>
  )
}

const InventoryViewSwitchTab = styled(DcSwitchTab)`
  width: max-content !important;
  height: 30px;
  box-sizing: border-box;
  align-items: stretch;

  button {
    flex: 0 0 auto;
    white-space: nowrap;
    line-height: 1.2;
  }
`

const groupedInventoryValue = (
  row: { warehouse_inventory?: Record<string, number | null> },
  field: 'on_hand' | 'available' | 'committed' | 'unavailable',
) =>
  row.warehouse_inventory?.[field] != null ? row.warehouse_inventory[field] : 0

const GroupedProductNameCell = ({
  name,
  variationCount,
}: {
  name?: string
  variationCount?: number
}) => {
  const count = variationCount ?? 0
  return (
    <GroupedProductMeta>
      <InventoryProductNameCell name={name} />
      <GroupedVariationCount>
        {count} {count === 1 ? 'variation' : 'variations'}
      </GroupedVariationCount>
    </GroupedProductMeta>
  )
}

const GroupedImageCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 4px 0;
`

const GroupedProductMeta = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 1px;
  min-width: 0;
  width: 100%;
`

const GroupedVariationCount = styled.div`
  font-size: 12px;
  line-height: 1.3;
  color: ${colors.grayMedium};
  white-space: nowrap;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

export default AdjustInventory
